import { type TechnologySelectDto } from '@@types/api.types';
import { CyberAssessmentHistoryList } from '@components/pages/technology/components/cyber/sidebar/cyber-assessment-history-list.component';
import {
  TECHNOLOGY_TAB_SIDEBAR_WIDTH_CLASS,
  TECHNOLOGY_TAB_STACK_LAYOUT_CLASS,
} from '@components/pages/technology/components/shared/technology-container-query.constants';
import { TechnologyTabLayout } from '@components/pages/technology/components/shared/technology-tab-layout.component';
import { TechnologyCyberCompletedReport } from '@components/pages/technology/components/technology-cyber-completed-report.component';
import { Tile } from '@components/shared/tile.component';
import { Skeleton } from '@components/ui/skeleton';
import React from 'react';
import { TechnologyCreateCyberAssessmentModal } from './technology-create-cyber-assessment-modal.component';
import { useTechnologyCyberTab } from './use-technology-cyber-tab.hook';

type TechnologyCyberTabProps = {
  technology: TechnologySelectDto;
};

export const TechnologyCyberTab: React.FC<TechnologyCyberTabProps> = (props) => {
  const { technology } = props;
  const {
    canCreate,
    createDisabledReason,
    cyberSection,
    cyberSidebarModel,
    effectiveCyberAssessment,
    emptyStateMessage,
    handleCreated,
    handleDiscardDraft,
    handleSectionChange,
    hasCyberAssessmentsTool,
    isCreateModalOpen,
    isDetailPending,
    isEmptyState,
    isFullWidthEmptyState,
    isPending,
    militaryTrlAssessments,
    navigateToDraftAssessment,
    openCreateModal,
    patchEvidencePackChecklistItem,
    selectedCyberAssessmentId,
    setIsCreateModalOpen,
    setSelectedAssessmentId,
  } = useTechnologyCyberTab({ technology });

  const renderMainContent = () => {
    if (isPending || isDetailPending) {
      return (
        <div className="flex flex-col gap-4">
          <Tile>
            <Skeleton className="h-48 w-full" />
          </Tile>
        </div>
      );
    }

    if (!effectiveCyberAssessment || isEmptyState) {
      return (
        <Tile
          className="min-h-72"
          emptyState={{
            message: emptyStateMessage,
            title: 'No Cyber Assessments',
          }}
          isEmpty
        />
      );
    }

    return (
      <TechnologyCyberCompletedReport
        assessment={effectiveCyberAssessment}
        canCreate={canCreate}
        cyberSection={cyberSection}
        onCreateAssessment={openCreateModal}
        onReviewAnswers={navigateToDraftAssessment}
        onSectionChange={handleSectionChange}
        onToggleChecklistItem={patchEvidencePackChecklistItem}
        technology={technology}
      />
    );
  };

  if (!hasCyberAssessmentsTool) {
    return (
      <Tile
        className="min-h-72"
        emptyState={{
          message: 'Cyber Assessments are not enabled for this account.',
          title: 'Feature Not Available',
        }}
        isEmpty
      />
    );
  }

  return (
    <>
      <TechnologyTabLayout
        className={TECHNOLOGY_TAB_STACK_LAYOUT_CLASS}
        mainContent={renderMainContent()}
        sidebar={
          isEmptyState && isFullWidthEmptyState ? undefined : (
            <CyberAssessmentHistoryList
              canCreate={canCreate}
              createDisabled={militaryTrlAssessments.length === 0}
              createDisabledReason={createDisabledReason}
              draftAssessment={cyberSidebarModel.draftAssessment}
              draftProgress={cyberSidebarModel.draftProgress}
              historyAssessments={cyberSidebarModel.historyAssessments}
              isLoading={isPending}
              onCreateAssessment={openCreateModal}
              onDiscardDraft={handleDiscardDraft}
              onResumeDraft={navigateToDraftAssessment}
              onSelectAssessment={setSelectedAssessmentId}
              selectedAssessmentId={selectedCyberAssessmentId}
            />
          )
        }
        sidebarClassName={TECHNOLOGY_TAB_SIDEBAR_WIDTH_CLASS}
        variant="sidebar-content"
      />

      <TechnologyCreateCyberAssessmentModal
        isOpen={isCreateModalOpen}
        militaryTrlAssessments={militaryTrlAssessments}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={handleCreated}
        technology={technology}
      />
    </>
  );
};
